import {Button, Chip, Dialog, DialogBody, DialogFooter, DialogHeader, Typography} from "@material-tailwind/react";
import React, {useContext, useEffect, useRef, useState} from "react";
import {ServicePaymentContext} from "../../context/service_payment";
import {updateCancelServicePayment, updateServicePayment} from "../../services/service/service_payment";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import dict from "../../helper/dict";

const MILESTONE_TABLE_HEAD = [
  "مرحله",
  "عنوان مرحله",
  "درصد تسویه",
  "سهم مباشر",
  "سهم پلتفرم",
  "ارزش افزوده",
  "جمع کل",
  "وضعیت",
  "زمان آماده سازی",
  "آخرین بروزرسانی",
  "عملیات"
];

function formatValue(value, isPrice = false) {
  if (value === null || value === undefined || value === "") return "-";
  return persianNumberFormat(value, isPrice);
}

function formatDateTime(value) {
  if (!value) return "-";
  return persianNumberFormat(value);
}

function getMilestoneRowClassName(status) {
  if (status === "settled") return "bg-green-50/60";
  if (status === "pending") return "bg-amber-50/50";
  if (status === "cancel") return "bg-red-50/60";
  return "";
}

function getServiceStatusDisplay(status) {
  if (status === "Ready to settle") {
    return {
      label: "آماده تسویه",
      color: "green",
    };
  }

  if (status === "Waiting for step") {
    return {
      label: "در انتظار تکمیل مرحله",
      color: "amber",
    };
  }

  if (status === "Settled") {
    return {
      label: "تسویه شده",
      color: "blue-gray",
    };
  }

  if (status === "Canceled") {
    return {
      label: "لغو و عودت شده",
      color: "red",
    };
  }

  if (status === "Closed") {
    return {
      label: "بسته شده",
      color: "gray",
    };
  }

  return {
    label: status || "-",
    color: "gray",
  };
}

function getMilestoneStatusDisplay(status) {
  if (status === "settled") {
    return {
      label: "تسویه شده",
      color: "green",
    };
  }

  if (status === "pending") {
    return {
      label: "در انتظار تکمیل مرحله",
      color: "amber",
    };
  }

  if (status === "eligible") {
    return {
      label: "آماده تسویه",
      color: "indigo",
    };
  }

  if (status === "cancel") {
    return {
      label: "لغو و عودت شده",
      color: "red",
    };
  }

  return {
    label: status || "-",
    color: "gray",
  };
}

export function ServicePaymentForm() {

  const {
    servicePayment,
    setLastDataRefresh,
    setOpen,
    open,
  } = useContext(ServicePaymentContext);

  const handleOpen = () => setOpen(!open);

  const [actionLoading, setActionLoading] = useState(null);
  const [cancelMilestone, setCancelMilestone] = useState(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const focusTimer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [open, servicePayment?.service_id]);

  const settleMilestone = async (milestone) => {
    if (actionLoading || milestone.status !== "eligible") return;
    
    try {
      setActionLoading({id: milestone.id, type: "settle"});
      let result = await updateServicePayment(milestone);

      if (result.status === 200) {
        setLastDataRefresh(Date.now());
      }
    } catch (err) {
      console.error('Service milestone settlement error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const cancelSelectedMilestone = async () => {
    if (actionLoading || !cancelMilestone) return;

    try {
      setActionLoading({id: cancelMilestone.id, type: "cancel"});
      let result = await updateCancelServicePayment(cancelMilestone);

      if (result.status === 200) {
        setCancelMilestone(null);
        setLastDataRefresh(Date.now());
      }
    } catch (err) {
      console.error('Service milestone cancel error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (!servicePayment) return null;

  return (
    <>
      <Dialog open={open} handler={handleOpen} size="xl"
              animate={{mount: {scale: 1, y: 0}, unmount: {scale: 0.9, y: -100}}}>
        <DialogHeader className="border-b flex justify-between">
          <Typography>جزئیات تسویه خدمت</Typography>
          {servicePayment.settlement_status &&
            <Chip
              variant="ghost"
              value={getServiceStatusDisplay(servicePayment.settlement_status).label}
              color={getServiceStatusDisplay(servicePayment.settlement_status).color}
            />
          }
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4 overflow-y-auto">

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="grid grid-row md:grid-col mt-2 border-b pb-2">
              <Typography color="blue-gray">
                {`شناسه خدمت: ${formatValue(servicePayment.service_id)}`}
              </Typography>
            </div>

            <div className="grid grid-row md:grid-col mt-2 border-b pb-2">
              <Typography color="blue-gray">
                {`${dict("user")}: ${servicePayment.person_name}`}
              </Typography>
            </div>

            <div className="grid grid-row md:grid-col mt-2 border-b pb-2">
              <Typography color="blue-gray">
                {`${dict("agent")}: ${servicePayment.agent_name}`}
              </Typography>
            </div>

            <div className="grid grid-row md:grid-col mt-2 border-b pb-2">
              <Typography color="blue-gray">
                {`${dict("branch")}: ${servicePayment.branch_name}`}
              </Typography>
            </div>

            <div className="grid grid-row md:grid-col mt-2 border-b pb-2">
              <Typography color="blue-gray">
                {`جمع کل: ${formatValue(servicePayment.total, true)}`}
              </Typography>
            </div>

            <div className="grid grid-row md:grid-col mt-2 border-b pb-2">
              <Typography color="blue-gray">
                {`مانده: ${formatValue(servicePayment.remaining_amount, true)}`}
              </Typography>
            </div>

            <div className="grid grid-row md:grid-col mt-2 border-b pb-2">
              <Typography color="blue-gray">
                {`لغو شده: ${formatValue(servicePayment.canceled_amount, true)}`}
              </Typography>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="mt-4 text-left w-full">
              <thead className="w-full">
              <tr className="w-full">
                {MILESTONE_TABLE_HEAD.map(head => (
                  <th key={head} className="border-y border-blue-gray-100 bg-blue-gray-50/50 p-3">
                    <Typography variant="small" color="blue-gray" className="font-normal textCenter">
                      {head}
                    </Typography>
                  </th>
                ))}
              </tr>
              </thead>
              <tbody className="textCenter">
              {(servicePayment.milestones || []).map((milestone, index) => {
                const isLast = index === servicePayment.milestones.length - 1;
                const classes = `${isLast ? "p-4" : "p-4 border-b border-blue-gray-50"} ${getMilestoneRowClassName(milestone.status)}`;
                const isActionLoading = Boolean(actionLoading);
                const isSettleDisabled = milestone.status !== "eligible" || isActionLoading;
                const isCancelDisabled = !["pending", "eligible"].includes(milestone.status) || isActionLoading;

                return (
                  <tr key={milestone.id}>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatValue(milestone.step_number || milestone.step)}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatValue(milestone.step_title)}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatValue(milestone.settlement_percent)}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatValue(milestone.agent_share, true)}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatValue(milestone.site_share, true)}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatValue(milestone.vat_share, true)}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatValue(milestone.total, true)}</Typography>
                    </td>
                    <td className={classes}>
                      <div className="flex justify-center">
                        <Chip
                          variant="ghost"
                          value={getMilestoneStatusDisplay(milestone.status).label}
                          color={getMilestoneStatusDisplay(milestone.status).color}
                          className="textCenter"
                        />
                      </div>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatDateTime(milestone.eligible_at)}</Typography>
                    </td>
                    <td className={classes}>
                      <Typography color="blue-gray">{formatDateTime(milestone.updated_at)}</Typography>
                    </td>
                    <td className={classes}>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant={milestone.status === "eligible" ? "gradient" : "outlined"}
                          color="gray"
                          size="sm"
                          disabled={isSettleDisabled}
                          loading={actionLoading?.id === milestone.id && actionLoading?.type === "settle"}
                          onClick={() => settleMilestone(milestone)}
                          className="ml-1"
                        >
                          {milestone.status === "settled" ? "انجام شده" :
                            milestone.status === "cancel" ? "لغو شده" :
                              actionLoading?.id === milestone.id && actionLoading?.type === "settle" ? "در حال تسویه" :
                                "تسویه"}
                        </Button>

                        <Button
                          variant="outlined"
                          color="red"
                          size="sm"
                          disabled={isCancelDisabled}
                          loading={actionLoading?.id === milestone.id && actionLoading?.type === "cancel"}
                          onClick={() => setCancelMilestone(milestone)}
                          className="ml-1"
                        >
                          {milestone.status === "cancel" ? "عودت شده" :
                            actionLoading?.id === milestone.id && actionLoading?.type === "cancel" ? "در حال لغو" :
                              "لغو"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>

        </DialogBody>

        <DialogFooter className="space-x-2">
          <Button ref={closeButtonRef} variant="text" color="gray" onClick={handleOpen}>
            <span>بستن</span>
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog
        open={Boolean(cancelMilestone)}
        handler={() => setCancelMilestone(null)}
        size="sm"
        animate={{mount: {scale: 1, y: 0}, unmount: {scale: 0.9, y: -100}}}
      >
        <DialogHeader className="border-b">
          <Typography>تایید لغو مرحله</Typography>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3">
          <Typography color="blue-gray">
            با لغو این مرحله، مبلغ این مایلستون از مانده رزرو خارج شده و به کیف پول کاربر بازگردانده می‌شود.
          </Typography>
          {cancelMilestone &&
            <Typography color="blue-gray" className="font-bold">
              {`مبلغ عودت: ${formatValue(cancelMilestone.total, true)}`}
            </Typography>
          }
        </DialogBody>
        <DialogFooter className="space-x-2">
          <Button
            variant="text"
            color="gray"
            disabled={Boolean(actionLoading)}
            onClick={() => setCancelMilestone(null)}
          >
            <span>انصراف</span>
          </Button>
          <Button
            variant="gradient"
            color="red"
            disabled={Boolean(actionLoading)}
            loading={actionLoading?.type === "cancel"}
            onClick={cancelSelectedMilestone}
            className="ml-1"
          >
            تایید لغو و عودت
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
}
